<template>
    <div class="te-shopping-cart">
        <div v-if="hasError && !isLoading" class="te-shopping-cart-error">
            <span>{{error.message}}</span>
        </div>

        <event-price-list
                v-if="state === 'event'"
                v-show="!isLoading"
                :sales-channel-id="salesChannelId"
                :register-id="registerId"
                :event-id="eventId"
                :order-id="activeOrderId"
                :customer-id="customerId"
                :preferred-language-code="preferredLanguageCode"
                :default-quantity="defaultQuantity"
                :language="language"
                @add="onAddToCart"
        ></event-price-list>

        <product-price-list
                v-if="state === 'product'"
                v-show="!isLoading"
                :sales-channel-id="salesChannelId"
                :register-id="registerId"
                :product-definition-id="productDefinitionId"
                :order-id="activeOrderId"
                :customer-id="customerId"
                :preferred-language-code="preferredLanguageCode"
                :default-quantity="defaultQuantity"
                :language="language"
                @add="onAddToCart"
        ></product-price-list>

        <cart v-if="state === 'cart'"
              v-show="!isLoading"
              :order-id="activeOrderId"
              :sales-channel-id="salesChannelId"
              :register-id="registerId"
              :customer-id="customerId"
              :preferred-language-code="preferredLanguageCode"
              :disable-payment-method-selection="disablePaymentMethodSelection"
              :disable-cart-editing="disableCartEditing"
              :language="language"
        ></cart>

        <rsvp v-if="state === 'rsvp'"
              v-show="!isLoading"
              :order-id="activeOrderId"
              :language="language"
        ></rsvp>

        <div class="te-cart-loading" v-if="isLoading">
            <div class="te-loading-container">
                <div class="te-loading-container-row">
                    <div class="te-loading-spinner"><div></div><div></div><div></div><div></div></div>
                </div>
                <div class="te-loading-container-row">
                    <span>{{loadingMessage}}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapGetters, mapActions } from "vuex";
    import EventPriceList from './EventPriceList'
    import ProductPriceList from './ProductPriceList'
    import * as jobTypes from "../store/job-types";
    import Cart from './Cart'
    import Rsvp from './Rsvp'
    import cart from "@/services/cart";
    // import moment from "moment";
    // import 'moment/locale/nl';

    export default {
        name: "ShoppingCart",

        components: {
            EventPriceList,
            ProductPriceList,
            Cart,
            Rsvp
        },

        props: {
            salesChannelId: {
                type: String,
                required: true
            },
            registerId: {
                type: String,
                required: true
            },
            eventId: {
                type: String,
            },
            productDefinitionId: {
                type: String,
            },
            orderId: {
                type: String,
            },
            customerId: {
                type: String
            },
            flow: {
                type: String,
                default: 'default'
            },
            defaultQuantity: {
                type: Number,
                default: 0
            },
            disablePaymentMethodSelection: {
                type: Boolean,
                default: false
            },
            disableCartEditing: {
                type: Boolean,
                default: false
            },
            clearOrderCacheOnFinalState: {
                type: Boolean,
                default: false
            },
            preferredLanguageCode: {
                type: String
            },
            locale: {
                type: String
            },
            addedToCart: {
                type: Function
            },
            finishedCartOperation: {
                type: Function
            },
            onFinishedAddingToCart: {
                type: Function
            },
            onFinishedCartChange: {
                type: Function
            },
            orderCompleted: {
                type: Function
            },
            loaded: {
                type: Function
            },
            onOrderLoaded: {
                type: Function
            },
            authUrl: {
                type: String
            },
            adminApiUrl: {
                type: String
            },
            graphApiUrl: {
                type: String
            },
            username: {
                type: String
            },
            oauthClientId: {
                type: String
            },
            oauthClientSecret: {
                type: String
            },
            language: {
                type: Object
            },
        },

        computed: {
            ...mapGetters({
                error: 'error',
                jobs: 'jobs',
                order: 'order',
                stateOrderId: 'orderId',
                // customerId: 'customerId'
            }),

            state: function() {
                // return this.eventId ? 'event' : 'cart'
                if(this.eventId) return 'event';
                if(this.productDefinitionId) return 'product';
                if(this.flow && this.flow === 'rsvp') return 'rsvp';
                return 'cart';
            },

            activeOrderId: function() {
                if(this.orderId) return this.orderId;
                if(this.stateOrderId) return this.stateOrderId;
                return null;
            },

            isLoading: function() {
                return (
                    this.jobs.includes(jobTypes.GET_PRICE_LIST) ||
                    this.jobs.includes(jobTypes.GET_EVENT) ||
                    this.jobs.includes(jobTypes.GET_OPT_IN_OPTIONS) ||
                    this.jobs.includes(jobTypes.GET_ORDER_MESSAGES) ||
                    this.jobs.includes(jobTypes.GET_OFFERS) ||
                    this.jobs.includes(jobTypes.GET_ORDER) ||
                    this.jobs.includes(jobTypes.CREATE_ORDER) ||
                    this.jobs.includes(jobTypes.CANCEL_ORDER) ||
                    this.jobs.includes(jobTypes.ADD_ORDER_TOKEN) ||
                    this.jobs.includes(jobTypes.CHECKOUT_ORDER) ||
                    this.jobs.includes(jobTypes.RSVP_ORDER) ||
                    this.jobs.includes(jobTypes.SAVE_ORDER_LINE) ||
                    this.jobs.includes(jobTypes.CHECK_PENDING_ORDER_LINES) ||
                    this.jobs.includes(jobTypes.CREATE_PAYMENT)
                );
            },

            loadingMessage: function() {
                let message = this.languageSettings.loading;
                if(this.jobs.includes(jobTypes.CHECK_PENDING_ORDER_LINES)) message = this.languageSettings.pendingItemsPleaseWait;
                if(this.jobs.includes(jobTypes.ADD_ORDER_TOKEN)) message = this.languageSettings.validatingToken;
                return message;
            },

            hasError: function() {
                return this.error && this.error.message !== null;
            },

            languageSettings: function() {
                const defaultSettings = {
                    // message
                    loading: 'Loading...',
                    pendingItems: 'Order has pending order line items. Please try again later.',
                    pendingItemsPleaseWait: 'Order has pending order line items. One moment please.',
                    noPrices: 'No prices found for event.',
                    noCapacity: 'No capacity available for event.',
                    validatingToken: 'Validating coupon',
                };
                return Object.assign(defaultSettings, this.language);
            },
        },

        methods: {
            ...mapActions([
                "getOrder",
                "setFinishedCartOperationCallback",
                "setOnFinishedAddingToCartCallback",
                "setOnFinishedCartChangeCallback",
                "setOrderCompletedCallback",
                "setOrderLoadedCallback",
                "setLocale",
                "setClearOrderCacheOnFinalState",
                "setCartErrorMessages",
                "clearOrder",
            ]),

            onAddToCart() {
                if(this.addedToCart) this.addedToCart();
            },

            async onLoad() {
                const count = await cart.getItemCount();
                if(this.loaded) this.loaded({order: {itemCount: count}});
            },

            setUrls() {
                if(this.authUrl) localStorage.setItem('te-auth-api-url', this.authUrl);
                if(this.adminApiUrl) localStorage.setItem('te-admin-api-url', this.adminApiUrl);
                if(this.graphApiUrl) localStorage.setItem('te-graph-api-url', this.graphApiUrl);
                if(this.username) localStorage.setItem('te-username', this.username);
                if(this.salesChannelId) localStorage.setItem('te-sales-channel-id', this.salesChannelId);
                if(this.registerId) localStorage.setItem('te-register-id', this.registerId);
                if(this.customerId) localStorage.setItem('te-customer-id', this.customerId);
                if(this.preferredLanguageCode) localStorage.setItem('te-preferred-language-code', this.preferredLanguageCode);
                // if(this.oauthClientId) localStorage.setItem('te-client-id', this.oauthClientId);
                localStorage.setItem('te-client-id', this.oauthClientId || 'shopping_cart');
                if(this.oauthClientSecret) localStorage.setItem('te-client-secret', this.oauthClientSecret);
            },

            setEvents() {
                if(this.finishedCartOperation) this.setFinishedCartOperationCallback({callback: this.finishedCartOperation});
                if(this.onFinishedAddingToCart) this.setOnFinishedAddingToCartCallback({callback: this.onFinishedAddingToCart});
                if(this.onFinishedCartChange) this.setOnFinishedCartChangeCallback({callback: this.onFinishedCartChange});
                if(this.orderCompleted) this.setOrderCompletedCallback({callback: this.orderCompleted});
                if(this.onOrderLoaded) this.setOrderLoadedCallback({callback: this.onOrderLoaded});
            },

            setCartErrorMessageTranslations() {
                this.setCartErrorMessages({
                    orderNotFound: this.language.orderNotFound,
                    customerNotFound: this.language.customerNotFound,
                    salesChannelNotFound: this.language.salesChannelNotFound,
                    registerNotFound: this.language.registerNotFound,
                    noPreferredLanguageSet: this.language.noPreferredLanguageSet,
                    retryAttemptsExceeded: this.language.retryAttemptsExceeded,
                    couldNotAddItems: this.language.couldNotAddItems,
                    createPaymentFailed: this.language.createPaymentFailed,
                });
            },


        },

        watch: {
            authUrl: function(url) {
                localStorage.setItem('te-auth-api-url', url);
            },
            adminApiUrl: function(url) {
                localStorage.setItem('te-admin-api-url', url);
            },
            graphApiUrl: function(url) {
                localStorage.setItem('te-graph-api-url', url);
            },
            username: function(user) {
                localStorage.setItem('te-username', user);
            },
            oauthClientId: function(clientId) {
                localStorage.setItem('te-client-id', clientId);
            },
            oauthClientSecret: function(clientSecret) {
                localStorage.setItem('te-client-secret', clientSecret);
            },
            salesChannelId: function(salesChannelId) {
                localStorage.setItem('te-sales-channel-id', salesChannelId);
            },
            registerId: function(registerId) {
                localStorage.setItem('te-register-id', registerId);
            },
            customerId: function(customerId) {
                localStorage.setItem('te-customer-id', customerId);
            },
            preferredLanguageCode: function(preferredLanguageCode) {
                localStorage.setItem('te-preferred-language-code', preferredLanguageCode);
            },
            language: function() {
                this.setCartErrorMessageTranslations();
            },
            locale: function() {
                // moment.locale(this.locale);
                if(this.locale) this.setLocale({locale: this.locale});
            },
            clearOrderCacheOnFinalState: function() {
                if(this.clearOrderCacheOnFinish) this.setClearOrderCacheOnFinalState({clearOrderCacheOnFinalState: this.clearOrderCacheOnFinalState});
            },
        },

        beforeMount() {
            if(this.locale) this.setLocale({locale: this.locale});
            if(this.clearOrderCacheOnFinalState) this.setClearOrderCacheOnFinalState({clearOrderCacheOnFinalState: this.clearOrderCacheOnFinalState});
        },

        created() {
            // if(this.locale) this.setLocale({locale: this.locale});
            // if(this.clearOrderCacheOnFinalState) this.setClearOrderCacheOnFinalState({clearOrderCacheOnFinalState: this.clearOrderCacheOnFinalState});
            localStorage.removeItem('te-customer-id');
            this.setUrls();
            this.setEvents();
        },

        mounted() {
            // if(this.locale) this.setLocale({locale: this.locale});
            // if(this.clearOrderCacheOnFinalState) this.setClearOrderCacheOnFinalState({clearOrderCacheOnFinalState: this.clearOrderCacheOnFinalState});
            this.setUrls();
            this.setEvents();
            this.onLoad();
            this.setCartErrorMessageTranslations();
            // moment.locale(this.locale);

          // register global event
          document.addEventListener('TicketEngineCartFlush', () => {
            this.clearOrder();
          })

        }
    }
</script>

<style scoped lang="scss">

</style>
